import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  AddRolloverAccountPortfolioMutation,
  AddRolloverAccountPortfolioMutationVariables,
  GetPerformanceQuery,
  GetPerformanceQueryVariables,
  GetPositionsQuery,
  GetPositionsQueryVariables,
  GetUserPortfolioQuery,
  GetUserPortfolioQueryVariables,
  GetUserPortfoliosQuery,
  GetUserPortfoliosQueryVariables,
  UpdateUserPortfolioMutation,
  UpdateUserPortfolioMutationVariables,
} from "types/graphql";

export function getPortfolios(
  client: ApolloClient<object>,
  accountId?: string | null
): Promise<GetUserPortfoliosQuery["getUserPortfolios"]> {
  return new Promise((resolve, reject) => {
    const PORTFOLIOS = gql`
      query getUserPortfolios($accountId: ID) {
        getUserPortfolios(accountId: $accountId) {
          recommended {
            assets {
              id
              symbol
              allocation
            }
            id
            name
            description
            createdAt
            updatedAt
            label
          }
          other {
            assets {
              id
              symbol
              allocation
            }
            id
            name
            description
            createdAt
            updatedAt
            label
          }
        }
      }
    `;

    const result = client.query<
      GetUserPortfoliosQuery,
      GetUserPortfoliosQueryVariables
    >({
      query: PORTFOLIOS,
      variables: {
        accountId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data.getUserPortfolios);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export function getActivePortfolio(
  client: ApolloClient<object>,
  accountId?: string | null
): Promise<GetUserPortfolioQuery["getUserPortfolio"]> {
  return new Promise((resolve, reject) => {
    const GET_USER_PORTFOLIO = gql`
      query getUserPortfolio($accountId: ID) {
        getUserPortfolio(accountId: $accountId) {
          assets {
            id
            symbol
            allocation
          }
          id
          name
          description
          createdAt
          updatedAt
        }
      }
    `;

    const variables: GetUserPortfolioQueryVariables = accountId
      ? { accountId }
      : {};

    const result = client.query<
      GetUserPortfolioQuery,
      GetUserPortfolioQueryVariables
    >({
      query: GET_USER_PORTFOLIO,
      variables,
    });
    result.then(
      (data) => {
        resolve(data.data.getUserPortfolio);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export const updateUserPortfolio = (
  client: ApolloClient<object>,
  { portfolioId, accountId }: { portfolioId: string; accountId?: string | null }
): Promise<UpdateUserPortfolioMutation["updateUserPortfolio"] | null> => {
  return new Promise((resolve, reject) => {
    const UPDATE_USER_PORTFOLIO = gql`
      mutation updateUserPortfolio($portfolioId: String!, $accountId: ID) {
        updateUserPortfolio(portfolioId: $portfolioId, accountId: $accountId) {
          assets {
            id
            symbol
            allocation
          }
          id
          name
          description
          createdAt
          updatedAt
        }
      }
    `;

    const variables: UpdateUserPortfolioMutationVariables = {
      portfolioId,
      ...(accountId && { accountId }),
    };

    const result = client.mutate<
      UpdateUserPortfolioMutation,
      UpdateUserPortfolioMutationVariables
    >({
      mutation: UPDATE_USER_PORTFOLIO,
      variables,
    });
    result.then(
      (data) => {
        resolve(data.data?.updateUserPortfolio ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
};

export function getPositions(
  client: ApolloClient<object>,
  accountId: string
): Promise<GetPositionsQuery["getPositions"]> {
  return new Promise((resolve, reject) => {
    const GET_POSITIONS_DATA = gql`
      query getPositions($accountId: ID!) {
        getPositions(accountId: $accountId) {
          dateTime
          positions {
            assetId {
              id
              name
              description
              type
              symbol
            }
            quantity
            amount
            currentMarketValue
            dateTime
          }
        }
      }
    `;
    const result = client.query<GetPositionsQuery, GetPositionsQueryVariables>({
      query: GET_POSITIONS_DATA,
      variables: { accountId },
    });
    result.then(
      (data) => {
        resolve(data.data.getPositions);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export function getPerformance(
  client: ApolloClient<object>,
  accountId: string,
  startTime: GetPerformanceQueryVariables["startTime"],
  endTime: GetPerformanceQueryVariables["endTime"]
): Promise<GetPerformanceQuery["getPerformance"]> {
  return new Promise((resolve, reject) => {
    const GET_PERFORMANCE_DATA = gql`
      query getPerformance(
        $accountId: ID!
        $startTime: DateTime!
        $endTime: DateTime!
      ) {
        getPerformance(
          accountId: $accountId
          startTime: $startTime
          endTime: $endTime
        ) {
          updatedAt
          categoryPerformances {
            category
            returns {
              returnAmount
              returnPercent
              startTime
              endTime
            }
            assetPerformances {
              returns {
                returnAmount
                returnPercent
                startTime
                endTime
              }
              assetId {
                id
                name
                description
                type
                symbol
              }
              marketValue
              totalAllocation
              categoryAllocation
            }
            marketValue
            allocation
          }
        }
      }
    `;
    const result = client.query<
      GetPerformanceQuery,
      GetPerformanceQueryVariables
    >({
      query: GET_PERFORMANCE_DATA,
      variables: { accountId, startTime, endTime },
    });
    result.then(
      (data) => {
        resolve(data.data.getPerformance);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export function addRolloverAccountPortfolio(
  client: ApolloClient<object>,
  portfolioId: string
): Promise<AddRolloverAccountPortfolioMutation | null> {
  return new Promise((resolve, reject) => {
    const SELECT_USER_ROLLOVER_PORTFOLIO = gql`
      mutation addRolloverAccountPortfolio($portfolioId: String!) {
        addRolloverAccountPortfolio(portfolioId: $portfolioId) {
          id
          routingNumber
          accountNumber
          name
          accountType
          isRollover
          state
          createdAt
          active
          balances {
            withdrawalBalance
            totalAccountBalance
            cashBalance
            principal
            totalPendingContributions
            totalPendingDistributions
          }
        }
      }
    `;
    const result = client.mutate<
      AddRolloverAccountPortfolioMutation,
      AddRolloverAccountPortfolioMutationVariables
    >({
      mutation: SELECT_USER_ROLLOVER_PORTFOLIO,
      variables: {
        portfolioId,
      },
    });
    result.then(
      (response) => {
        resolve(response.data ?? null);
      },
      (errors: Error) => {
        reject(errors);
      }
    );
  });
}

export default {
  getPortfolios,
  updateUserPortfolio,
  // getPositions,
  getPerformance,
  addRolloverAccountPortfolio,
};
